import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import * as ExpoFileLogger from 'expo-file-logger';
import AppContext, { AppActions, AppEvents, AppStatus } from '../../contexts/App';
import LayoutContext, { MODERN_LAYOUT_COLLAPSE_WIDTH } from '../../contexts/Layout';
import { findActive } from '../../hooks/useNavigation';
import { UmbracoComponentMapping } from '../../Mapping';
import { TopMenu } from '../TopMenu';
import { Alert } from '../../componentsv2/common/Alert';
import Authentication from '../../componentsv2/common/Authentication';
import LoggedComponent from '../../componentsv2/common/LoggedComponent';
import SearchResults from '../../componentsv2/common/SearchResults';
import SiteFooter from '../../componentsv2/cms/SiteFooter';
import SlidingViewContainer from '../../componentsv2/common/SlidingViewContainer';
import UnsupportedPage from '../../componentsv2/common/UnsupportedPage';
import UserProfile from '../../componentsv2/common/UserProfile';
import Secondary from '../../componentsv2/navigation/Secondary';
import Ancillary from '../../componentsv2/navigation/Ancillary';
import Utils from '../../common/Utils';
import HardDimensionsContainer from '../../componentsv2/common/HardDimensionsContainer';
import ElasticViewContainer from '../../componentsv2/common/ElasticViewContainer';

const RightFrame = ({ followContentLink, physicalWidth, width = '100%' }) => {
    const {
        state: { action, navigation, search, settings }
    } = useContext(AppContext);
    const { state } = useContext(AppContext);
    const { layout } = useContext(LayoutContext);
    const isCollapsed = navigation.primaryIsCollapsed;
    const clonedViewRef = useRef(null);
    const scrollViewRef = useRef(null);
    const cornerBlocksRef = useRef({});
    const searchSubmitted = search?.event?.equals(AppEvents.SEARCH_QUERY_SUBMITTED);
    const shouldRenderSecondary =
        navigation?.secondary[findActive(action.href || window.location.pathname)] &&
        Object.keys(navigation?.secondary[findActive(action.href || window.location.pathname)]).length > 0;
    const lastHrefRef = useRef(state.action.href);
    const [isAuthVisible, setIsAuthVisible] = useState();

    const THREE_UP_SECONDARY_NAV_WIDTH = 0.3; // @tim.cook I'm not sure where this should live @jacksonkr
    const SCROLL_VIEW_MIN_WIDTH_PERCENT = 0.6;

    useEffect(() => {
        scrollViewRef.current?.scrollTo({
            y: 0,
            animated: true
        });
    }, [action, action?.href]);

    useEffect(() => {
        if (action.type?.equals(AppActions.NAVIGATE_INTERNAL)) {
            setIsAuthVisible(true);
        } else {
            setIsAuthVisible(false);
        }
    }, [action, isCollapsed]);

    const pageComponents = Object.values(action.data?.page?.contentItems || {}).map(cmp => {
        const UmbracoComponent = UmbracoComponentMapping[cmp.nodeTypeAlias];
        const componentWidth = '100%';

        if (UmbracoComponent) {
            const { key, rest } = Utils.splitKeyFromProps(cmp);

            return (
                <LoggedComponent key={rest.id} name={rest.nodeTypeAlias} cacheHit={action.data?.cacheHit}>
                    <HardDimensionsContainer width={componentWidth}>
                        <UmbracoComponent {...rest} followContentLink={followContentLink} cmsKey={key} references={action?.data?.references} physicalWidth={physicalWidth} />
                    </HardDimensionsContainer>
                </LoggedComponent>
            );
        }
    });

    /**
     * used to disable sliding animation for secondary menu in certain cases @jacksonkr
     */
    const isParentOfLastPage = useCallback(() => {
        let ret = false;
        if (lastHrefRef.current === state.action.href) ret = false;
        else if (lastHrefRef.current?.includes(state.action.href)) ret = true;
        lastHrefRef.current = state.action.href;

        return ret;
    }, [lastHrefRef, state.action.href]);

    const renderContent = () => {
        if (search?.term && searchSubmitted) {
            ExpoFileLogger.log('debug', 'Rendering SearchResults');

            return <SearchResults followContentLink={followContentLink} width={width} />;
        } else if ((action.internalComponent && action.internalComponent.indexOf('Profile') !== -1) || window.location.pathname?.indexOf('user-profile') !== -1) {
            return (
                <SlidingViewContainer clonedContentRef={clonedViewRef} sid={1}>
                    <UserProfile width={width} />
                </SlidingViewContainer>
            );
        } else {
            // const contentItems = action.data.contentItems || action.data.page.contentItems;
            // if (contentItems) {
            if (action.data?.page?.developmentOnly && settings?.websiteSettings?.environment?.equals('Production')) {
                return <UnsupportedPage goBack={followContentLink} />;
            } else {
                ExpoFileLogger.log('debug', `Rendering View ${action.data?.page?.title}`);

                const isLogin = isAuthVisible && action.internalComponent?.indexOf('Login') !== -1 ? true : false;
                return (
                    <View style={{ flex: 1, flexDirection: 'row', width: width }}>
                        {shouldRenderSecondary && (
                            <ElasticViewContainer width={isCollapsed ? THREE_UP_SECONDARY_NAV_WIDTH * 100 + '%' : width}>
                                <SlidingViewContainer clonedContentRef={clonedViewRef} animate={!isCollapsed && !isParentOfLastPage()} sid={2}>
                                    <View style={{ height: '100%', overflow: 'hidden' }}>
                                        <Secondary followContentLink={followContentLink} {...{ cornerBlocksRef }} />
                                    </View>
                                </SlidingViewContainer>
                            </ElasticViewContainer>
                        )}
                        {pageComponents.length && (
                            <SlidingViewContainer clonedContentRef={clonedViewRef} sid={3}>
                                <ScrollView
                                    contentContainerStyle={{ minWidth: `${SCROLL_VIEW_MIN_WIDTH_PERCENT * 100}vw`, flexGrow: 1 }}
                                    ref={scrollViewRef}
                                    showsVerticalScrollIndicator={false}
                                >
                                    {pageComponents?.map(cmp => cmp)}
                                    <LoggedComponent name='SiteFooter'>
                                        <SiteFooter followContentLink={followContentLink} />
                                    </LoggedComponent>
                                </ScrollView>
                                <Authentication isLogin={isLogin} isRegister={!isLogin} setVisible={setIsAuthVisible} visible={isAuthVisible} />
                                {action.type?.equals(AppActions.USER_LOGOUT) &&
                                    action.state.status.equals(AppStatus.USER_LOGOUT_SUCCESS) &&
                                    Alert('You have been logged out.')}
                                {action.type?.equals(AppActions.USER_LOGOUT) &&
                                    action.state.status.equals(AppStatus.USER_LOGOUT_FAILED) &&
                                    Alert('Logout failed. Please try again.')}
                            </SlidingViewContainer>
                        )}
                    </View>
                );
            }
        }
    };

    return (
        <View style={StyleSheet.flatten(Styles.container, { width: layout.dimensions.width >= MODERN_LAYOUT_COLLAPSE_WIDTH ? width : '100%' })}>
            <TopMenu>
                <Ancillary />
            </TopMenu>
            <View style={Styles.wrapper}>{renderContent()}</View>
        </View>
    );
};

const Styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        flex: 1,
        height: '100%',
        justifyContent: 'space-evenly'
    },
    contentItemWrapper: {
        marginBottom: 10
    },
    wrapper: {
        alignItems: 'center',
        height: '100%',
        width: '100%'
    }
});

export default RightFrame;
